// react
import React from 'react';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';

// mui
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// utils
import { formatRelativeTime } from '@karehero/helpers/format';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  deepLink?: string;
  readAt?: string | null;
  createdAt: string;
}

export interface NotificationsPageProps {
  notifications: NotificationItem[];
  onNotificationClick: (notification: NotificationItem) => void;
  onMarkRead?: (notification: NotificationItem) => void;
  onMarkUnread?: (notification: NotificationItem) => void;
  onBack?: () => void;
  isLoading?: boolean;
}

/**
 * NotificationsPage displays a list of notifications in reverse-chronological
 * order with read/unread distinction.
 */
export const NotificationsPage = ({
  notifications,
  onNotificationClick,
  onMarkRead,
  onMarkUnread,
  onBack,
  isLoading,
}: NotificationsPageProps) => {
  const theme = useTheme();

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={<PageHeader title='Notifications' />}
        contentSlot={
          <Stack spacing={1.5}>
            {notifications.length === 0 && !isLoading && (
              <Stack
                spacing={2}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                }}
              >
                <Icon
                  icon='bell'
                  fontSize={40}
                  color={theme.palette.text.disabled}
                />
                <Typography
                  variant='body1'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No notifications yet
                </Typography>
              </Stack>
            )}

            {notifications.map((notification) => {
              const isUnread = !notification.readAt;

              return (
                <Card
                  key={notification.id}
                  variant='outlined'
                  elevation={0}
                  onClick={() => onNotificationClick(notification)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack
                      direction='row'
                      spacing={1.5}
                      sx={{
                        alignItems: 'flex-start',
                      }}
                    >
                      {isUnread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            flexShrink: 0,
                            mt: 1,
                          }}
                        />
                      )}
                      <Stack
                        spacing={0.5}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Stack
                          direction='row'
                          sx={{
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                          }}
                        >
                          <Typography
                            variant='body1'
                            noWrap
                            sx={{
                              fontWeight: isUnread ? 600 : 400,
                              flex: 1,
                              mr: 1,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Typography
                            variant='caption'
                            sx={{
                              color: 'text.secondary',
                              flexShrink: 0,
                            }}
                          >
                            {formatRelativeTime(notification.createdAt)}
                          </Typography>
                        </Stack>
                        {notification.body && (
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'text.secondary',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {notification.body}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                  {((isUnread && onMarkRead) ||
                    (!isUnread && onMarkUnread)) && (
                    <Stack
                      direction='row'
                      sx={{
                        justifyContent: 'flex-end',
                        px: 2,
                        pb: 1,
                      }}
                    >
                      <Button
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isUnread) {
                            onMarkRead?.(notification);
                          } else {
                            onMarkUnread?.(notification);
                          }
                        }}
                      >
                        {isUnread ? 'Mark as read' : 'Mark as unread'}
                      </Button>
                    </Stack>
                  )}
                </Card>
              );
            })}

            {isLoading && (
              <Stack
                sx={{
                  alignItems: 'center',
                  py: 3,
                }}
              >
                <CircularProgress size={24} />
              </Stack>
            )}
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

NotificationsPage.displayName = 'NotificationsPage';
export default NotificationsPage;
